import { Auth } from 'aws-amplify';
import { message } from 'antd';
import hybridStorage from '../../../utils/hybridStorage';
import { isEnabledUserStrict } from './enabledUserFilter';

export const DISABLED_ACCOUNT_MESSAGE =
  'This account has been disabled. Contact your administrator to regain access.';

/**
 * Reject a session belonging to a disabled account.
 *
 * DEFENSE IN DEPTH ONLY. Cognito and the API are the real boundary — disabling a user is a
 * pure database operation (disableDashboardUser deletes their ENABLED role item) that never
 * touches Cognito, so their password still authenticates and anyone editing this bundle still
 * gets a valid session. This exists to close the hole for normal users and for cached bundles
 * once the backend starts returning 403; that 403 already routes through AUTH_ERROR_CODES →
 * forceSignOut() in App.js and ejects every open tab on its own.
 *
 * Pass the raw `authenticatedUser` payload from the AUTHENTICATED_USER response — NEVER
 * localStorage.userData, which is an org-preview snapshot that is stale by construction, can
 * be hand-edited in devtools, and carries no `roles` key at all.
 *
 * @returns {Promise<boolean>} true when the account is still enabled. On false the local
 * session has already been torn down, and the caller must not write isAuthenticated: true.
 */
export const enforceEnabledUser = async (authenticatedUser, { notify = true } = {}) => {
  if (isEnabledUserStrict(authenticatedUser)) return true;

  // Breadcrumb for Highlight — makes an accidental lockout identifiable from a session replay
  // instead of a support ticket that says "I can't log in".
  console.error('[auth] blocked a session: user has no ENABLED role item', {
    id: authenticatedUser && authenticatedUser.id,
    roleCount: ((authenticatedUser && authenticatedUser.roles) || []).length,
  });

  try {
    localStorage.removeItem('userData');
  } catch (err) {
    // private mode / quota — nothing to clean up
  }

  // Embeddable sessions live in sessionStorage only (hybridStorage.setItem skips the
  // localStorage backup for them). Clearing the primary session here would sign the admin
  // out of their own, unrelated tab in the same browser.
  if (!hybridStorage.isEmbeddable()) hybridStorage.clearPrimarySession();

  try {
    await Auth.signOut();
  } catch (err) {
    console.error('[auth] Auth.signOut failed while ejecting a disabled user', err);
  }

  if (notify) message.error(DISABLED_ACCOUNT_MESSAGE);

  return false;
};
